/* ------------------- IMPORTS */
import styled from '@emotion/styled'
import { Box, Icons } from 'components'
import { useCreatePostStore } from 'stores/CreatePostStore'
import { IPostVariant } from 'types/PostInterface'
import { MediaContainerPost } from '../../GenericComponents/MediaContainerPost'
import { AppStore, T } from 'utils'
import { truncateText } from 'pages/ai-discover/commonFunction'

/* ------------------- INTERFACES  */
interface Props {
  postPreview?: IPostVariant
}

export const ResultsPreviewLinkedin = ({ postPreview }: Props) => {
  /* ----------------- ZUSTAND  */
  const { activePost } = useCreatePostStore((s) => ({
    activePost: s.activePost as IPostVariant,
  }))

  const post = postPreview ?? activePost

  const urls = post?.data?.urls

  const metadataVisibleIndex = urls && urls.findIndex((url) => url.metaData.visible === true)

  const metadata = urls && metadataVisibleIndex !== -1 && urls[metadataVisibleIndex!]

  return (
    <ResultsPreviewLinkedinContainer>
      <HeaderPreview>
        <ProfileContent>
          <ProfileInfo>
            <ProfileImg src={post?.info?.account?.picture} />

            <ProfileMinorInfo>
              <ProfileName>{truncateText(post?.info?.account?.name, 40)}</ProfileName>

              <ProfileWork>Graphic Designer Freelance</ProfileWork>

              <ProfileDate>
                {new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })} &#x2022;
                <Icons.fbMap />
              </ProfileDate>
            </ProfileMinorInfo>
          </ProfileInfo>

          <Icons.threeDots />
        </ProfileContent>

        <PreviewText>
          {post?.data?.text?.length > 80 ? (
            <>
              {post?.data?.text?.slice(0, 80)} <SeeMoreText>... {T.postPage.more}</SeeMoreText>
            </>
          ) : (
            post?.data?.text
          )}
        </PreviewText>
      </HeaderPreview>

      {postPreview && !metadata && post?.data?.medias?.length === 0 ? (
        <></>
      ) : (
        <MediaContainerPost width={'98%'} height={390} arrowsPositions={20} postPreview={postPreview} />
      )}

      <FooterPreview>
        <UpperFooter>
          <Feedbacks>
            {AppStore.darkTheme ? (
              <Icons.facebookLikeDarkMode style={{ width: 35, height: 35 }} />
            ) : (
              <Icons.facebookLike style={{ width: 35, height: 35 }} />
            )}
            113
          </Feedbacks>

          <Reactions>
            {T.postPage.comments}: 10
            <span style={{ marginLeft: 5 }} /> &#x2022; {T.postPage.reposts}: 7
          </Reactions>
        </UpperFooter>

        <LowerFooter>
          <InteractionContainer>
            <Icons.linkedinLike fill={AppStore.theme.o.black} />
            {T.postPage.like}
          </InteractionContainer>

          <InteractionContainer>
            <Icons.linkedinComment fill={AppStore.theme.o.black} /> {T.postPage.comment}
          </InteractionContainer>

          <InteractionContainer>
            <Icons.linkedinRepost fill={AppStore.theme.o.black} /> {T.postPage.reposts}
          </InteractionContainer>

          <InteractionContainer>
            <Icons.linkedinSend fill={AppStore.theme.o.black} /> {T.postPage.send}
          </InteractionContainer>
        </LowerFooter>
      </FooterPreview>
    </ResultsPreviewLinkedinContainer>
  )
}

const ResultsPreviewLinkedinContainer = styled(Box)`
  width: 100%;
  border: 1px solid ${() => AppStore.theme.o.grey};
`

const HeaderPreview = styled(Box)`
  padding: 12px;
  gap: 8px;
`

const PreviewText = styled(Box)`
  color: ${() => AppStore.theme.o.black};
  border-radius: 0;
  display: -webkit-box !important;
  -webkit-box-orient: vertical;
  word-wrap: break-word;
`

const SeeMoreText = styled.span`
  margin-left: 4px;
  color: ${() => AppStore.theme.o.darkGrey};
`

const ProfileContent = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const ProfileInfo = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const ProfileImg = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
`

const ProfileMinorInfo = styled(Box)`
  gap: 4px;
`

const ProfileName = styled(Box)`
  color: ${() => AppStore.theme.o.black};
  font-family: Roboto;
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  letter-spacing: 0.3px;
`

const ProfileWork = styled(Box)`
  color: ${() => AppStore.theme.o.black};
  font-family: Roboto;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.36px;
`

const ProfileDate = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  color: ${() => AppStore.theme.o.darkGrey};
  font-family: Roboto;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.26px;
`

const FooterPreview = styled(Box)`
  margin-top: 4px;
  padding: 0 12px;
`

const UpperFooter = styled(Box)`
  padding: 8px 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-radius: 0px;
  border-bottom: 1px solid ${() => AppStore.theme.o.grey};
  position: relative;

  color: ${() => AppStore.theme.o.darkGrey};
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 13.002px; /* 81.264% */
`

const Feedbacks = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  position: absolute;
`

const Reactions = styled(Box)`
  display: flex;
  flex-direction: row;
  margin-left: auto;
`

const LowerFooter = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 8px;
`

const InteractionContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  color: ${() => AppStore.theme.o.black};
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 13.002px; /* 81.264% */
`
